import { useCallback, useState, useEffect } from "react";
import { useReactFlow } from "react-flow-renderer";
import { useProcessEditorContext } from "../../../../components/ProcessEditor/ProcessEditorContext";
import { NodeTypes } from "../../../../components/ProcessEditor/types";
import type { Node } from "../../../../components/ProcessEditor/utils/createNodes";
import type { Field } from "@certa/studio-core";
import {
  ANIMATION_DURATION,
  ZOOM_LEVEL
} from "./useStudio2ComponentInView.constants";
import type { FocusNodeConfig } from "../../useStudioAPI.types";
import { useStudioAPIStore } from "../../store";

/** Handles bringing component into view */
export const useStudio2ComponentInView = () => {
  const { nodes } = useProcessEditorContext();
  const setCollapsedNodes = useStudioAPIStore(state => state.setCollapsedNodes);

  const { setCenter, getNode } = useReactFlow();

  const [triggerManualExpandAndCenter, setTriggerManualExpandAndCenter] =
    useState<string | null>(null);

  /**
   * Pans canvas to bring the specified component to center of viewport.
   * Parent components will be expanded automatically.
   **/
  const expandAndCenterNode = useCallback(
    (node: Node, { xOffset = 0, yOffset = 0 }: FocusNodeConfig = {}) => {
      const nodeType = node?.type;
      const centerOptions = {
        zoom: ZOOM_LEVEL,
        duration: ANIMATION_DURATION
      };

      if (nodeType === NodeTypes.STEPGROUP) {
        const x = node.position.x + Number(node.style?.width) / 2 - xOffset;
        const y = node.position.y + Number(node.style?.height) / 2 - yOffset;

        if (x && y) {
          setCenter(x, y, centerOptions);
        }
      }

      if (nodeType === NodeTypes.STEP) {
        if (!node.parentNode) return;
        const swimlane = getNode(node.parentNode) as Node | undefined;
        if (!swimlane) return;

        const x =
          node.position.x +
          Number(node.style?.width) / 2 +
          swimlane.position.x -
          xOffset;
        const y =
          node.position.y +
          Number(node.style?.height) / 2 +
          swimlane.position.y -
          yOffset;

        if (x && y) {
          setCenter(x, y, centerOptions);
        }

        // Expand parent swimlane if not expanded
        if (!swimlane.expanded) {
          setCollapsedNodes(prev => prev.filter(id => id !== swimlane.uid));
        }
      }

      if (nodeType === NodeTypes.FIELD) {
        if (!node.parentNode) return;
        const step = getNode(node.parentNode) as Node | undefined;
        if (!step) return;

        if (!step.parentNode) return;
        const swimlane = getNode(step.parentNode) as Node | undefined;
        if (!swimlane) return;

        const x =
          node.position.x +
          Number(node.style?.width) / 2 +
          step.position.x +
          swimlane.position.x -
          xOffset;
        const y =
          node.position.y +
          Number(node.style?.height) / 2 +
          step.position.y +
          swimlane.position.y -
          yOffset;

        if (x && y) {
          setCenter(x, y, centerOptions);
        }
      }
    },
    [getNode, setCenter, setCollapsedNodes]
  );

  const getNodeFromComponentId = useCallback(
    (componentId: string) => {
      const node = nodes.find(node => node.uid === componentId);
      return node;
    },
    [nodes]
  );

  const expandParents = useCallback(
    (componentId: string) => {
      // If the component is a step node, expand the swimlane and return.
      const stepNode = nodes.find(node => node?.data?.step?.id === componentId);

      if (stepNode) {
        const swimlane = getNode(stepNode?.parentNode || "") as
          | Node
          | undefined;
        if (!swimlane) return;
        setCollapsedNodes(prev => prev.filter(id => id !== swimlane.uid));
        return;
      }

      //If the component is a field node, expand the step and swimlane.
      const stepNodeContainingTheField = nodes.find(node =>
        node.data?.step?.fields?.find(
          (field: Field) => field.id === componentId
        )
      );
      if (!stepNodeContainingTheField) return;

      if (!stepNodeContainingTheField.parentNode) return;
      const swimlane = getNode(stepNodeContainingTheField.parentNode) as
        | Node
        | undefined;
      if (!swimlane) return;

      setCollapsedNodes(prev =>
        prev.filter(
          id => id !== stepNodeContainingTheField.uid && id !== swimlane.uid
        )
      );
      return;
    },
    [getNode, nodes, setCollapsedNodes]
  );

  const setComponentInView = useCallback(
    (componentId: string, config?: FocusNodeConfig) => {
      const node = getNodeFromComponentId(componentId);

      // If node is not found (e.g. field nodes are not available until
      // parent is expanded), try to expand the parent node (i.e. step)
      if (!node) {
        expandParents(componentId);
        setTriggerManualExpandAndCenter(componentId);
      } else {
        expandAndCenterNode(node, config);
      }
    },
    [expandAndCenterNode, getNodeFromComponentId, expandParents]
  );

  // Try to find node (field) again after parent nodes (step and swimlane) is expanded
  useEffect(() => {
    if (triggerManualExpandAndCenter) {
      const node = getNodeFromComponentId(triggerManualExpandAndCenter);
      if (node) {
        expandAndCenterNode(node);
        setTriggerManualExpandAndCenter(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerManualExpandAndCenter, nodes]);

  return { setComponentInView, expandParents };
};
